/**
 * 業務通知テンプレ一覧 + 編集ダイアログ (Phase B / 代案E, v3.2.21 で chat 統合)。
 *
 * イベント別 (1イベント=1テンプレ) のテンプレを後日編集可能。
 * 件名/本文に {{variable}} 形式で差し込み変数を埋め込む。
 *
 * v3.2.21 変更:
 *  - メール / チャット の 2 セクションを 1 ドキュメントで管理
 *  - 編集モーダル: 「メール」「チャット」タブ切替
 *  - チャットは text or card 形式、card にはリンクボタン 0〜5 個
 *  - 通知先 Webhook (chat_endpoints) を選択
 *  - リスト: メール / チャット の有効化アイコン両方表示
 */
import type {
  BusinessChatButton,
  BusinessChatFormat,
  BusinessMailEvent,
  BusinessNotificationAudience,
} from '@goenhub/shared';
import {
  BUSINESS_MAIL_AVAILABLE_VARS,
  BUSINESS_MAIL_EVENT_LABEL,
  EVENT_AUDIENCE_DEFAULTS,
  EVENT_TIMING_LABEL,
} from '@goenhub/shared';
import {
  Bell,
  Clock,
  Copy,
  Edit,
  Layers,
  Mail,
  MessageSquare,
  Plus,
  Save,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  useBusinessMailTemplates,
  useDeleteBusinessMailTemplate,
  useUpsertBusinessMailTemplate,
  type BusinessMailTemplateRecord,
  type UpsertConflict,
} from '@/hooks/useBusinessMailTemplates';
import { useChatEndpoints } from '@/hooks/useChatEndpoints';
import { useProductCategories } from '@/hooks/useProductCategories';

const ALL_EVENTS: BusinessMailEvent[] = [
  'briefing_applied',
  'briefing_confirmed',
  'quote_sent',
  'invoice_sent',
  'deal_won',
  'invoice_paid',
  'shipped',
  'delivered',
  'document_auto_reply',
  'po_quote_request',
  'po_order_request',
  'po_transfer_request',
  'commission_payment_completed',
  'commission_invoice_received',
  'commission_invoice_paid',
  'commission_invoice_reminder',
  // v3.2.22
  'commission_payment_request',
  'form_submitted',
  'deal_lost',
  'commission_record_created',
  // v3.2.25 (同意ステップ)
  'consent_send',
  'consent_signed',
  // v3.2.30 (外部SA管理)
  'sa_provisional',
  'sa_registered',
  'sa_active',
  'sa_renewal_reminder',
  'sa_renewal_due_internal',
  'sa_renewed',
  // 2026-07-28 (SA 顧客保有期間)
  'sa_customer_retention_reminder',
];

/** 既定メールテンプレ */
const DEFAULT_MAIL: Record<BusinessMailEvent, { subject: string; body: string }> = {
  briefing_applied: {
    subject: '【{{vendorName}}】個別説明の申込が入りました',
    body: `{{assigneeName}} 様\n\nあなたが担当する個別説明枠に申込が入りました。\n\n申込者: {{customerName}} 様\nフリガナ: {{customerKana}}\nメール: {{customerEmail}}\nフォーム: {{formTitle}}\n参加人数: {{participantCount}}名\n\n確認/知りたいこと:\n{{inquiryNotes}}\n\n候補日程:\n{{candidateDates}}\n\n管理画面で確認・日程確定を行ってください:\n{{adminUrl}}\n\n{{vendorName}}`,
  },
  briefing_confirmed: {
    subject: '【{{vendorName}}】個別説明会のご案内',
    body: `{{customerName}} 様\n\nこのたびは個別説明会へのお申込みありがとうございます。\n下記日程にて確定いたしましたのでご案内いたします。\n\n日時: {{date}} {{startTime}}〜{{endTime}}\nスタイル: {{style}}\n{{zoomUrl}}\n{{location}}\n\n担当: {{assigneeName}}\n何かございましたらお気軽にお問い合わせください。\n\n{{vendorName}}`,
  },
  quote_sent: {
    subject: '【{{vendorName}}】見積書のお知らせ ({{quoteTitle}})',
    body: `{{customerName}} 様\n\nお問い合わせいただきました件につきまして、見積書を下記の通りお送りいたします。\n\n件名: {{quoteTitle}}\n合計 (税込): {{totalAmount}}\n有効期限: {{validUntil}}\n\n見積書のWeb表示はこちら:\n{{publicUrl}}\n\nご不明な点がございましたら、お気軽にお問い合わせください。\n\n{{vendorName}}`,
  },
  invoice_sent: {
    subject: '【{{vendorName}}】請求書のお知らせ ({{invoiceNumber}})',
    body: `{{customerName}} 様\n\n下記の通り、ご請求書をお送りいたします。\n\n請求番号: {{invoiceNumber}}\n件名: {{invoiceTitle}}\nご請求金額 (税込): {{amount}}\n支払期限: {{dueDate}}\n\n請求書のWeb表示はこちら:\n{{publicUrl}}\n\nご不明な点がございましたら、お気軽にお問い合わせください。\n\n{{vendorName}}`,
  },
  deal_won: {
    subject: '【{{vendorName}}】ご契約のお礼',
    body: `{{customerName}} 様\n\nこのたびは弊社製品をご契約いただき誠にありがとうございます。\n詳細は順次ご連絡いたします。\n\n{{vendorName}}`,
  },
  invoice_paid: {
    subject: '【{{vendorName}}】お支払い確認のご連絡',
    body: `{{customerName}} 様\n\n請求書 {{invoiceNumber}} のお支払いを確認いたしました。\n金額: {{amount}}\n\nありがとうございます。\n\n{{vendorName}}`,
  },
  shipped: {
    subject: '【{{vendorName}}】商品出荷のお知らせ',
    body: `{{customerName}} 様\n\nご注文いただいた商品を発送いたしました。\n\n配送業者: {{carrierName}}\n追跡番号: {{trackingNumber}}\n発送番号: {{deliveryNumber}}\n\n到着までいましばらくお待ちください。\n\n{{vendorName}}`,
  },
  delivered: {
    subject: '【{{vendorName}}】納品完了のご報告と検収のお願い',
    body: `{{customerName}} 様\n\n下記商品の納品が完了いたしました。\n{{productSummary}}\n\nお手数ですが、ご検収のほどよろしくお願いいたします。\n\n{{vendorName}}`,
  },
  document_auto_reply: {
    subject: '【{{vendorName}}】資料ダウンロードのご案内',
    body: `{{customerName}} 様\n\n資料請求いただきありがとうございます。\n下記URLよりご覧いただけます。\n\n資料: {{documentTitle}}\nURL: {{publicUrl}}\n{{passwordHint}}\n\n{{vendorName}}`,
  },
  po_quote_request: {
    subject: '【見積依頼】{{dealTitle}}',
    body: `お世話になっております。{{vendorName}} の {{assigneeName}} です。\n\n下記案件につきまして、お見積をお願いいたします。\n\n案件: {{dealTitle}}\nお客様: {{customerName}}\n内容:\n{{productSummary}}\n\nご検討のほどよろしくお願いいたします。\n\n{{vendorName}}\n{{assigneeName}}`,
  },
  po_order_request: {
    subject: '【正式発注】{{invoiceNumber}}',
    body: `お世話になっております。{{vendorName}} の {{assigneeName}} です。\n\n下記の通り正式発注いたします。送金は手配済みです。\n\n請求番号: {{invoiceNumber}}\n合計: {{totalAmount}}\n値引: {{discountAmount}}\n最終金額: {{finalAmount}}\nお客様: {{customerName}}\n\nよろしくお願いいたします。\n\n{{vendorName}}\n{{assigneeName}}`,
  },
  po_transfer_request: {
    subject: '【メーカー代金 送金申請】{{invoiceNumber}}',
    body: `経理ご担当者様\n\n下記の通り、メーカーへの製品代金送金を申請いたします。\n\n請求番号: {{invoiceNumber}}\n合計: {{totalAmount}}\n値引: {{discountAmount}}\n送金額: {{finalAmount}}\nお客様: {{customerName}}\n備考: {{memo}}\n\nよろしくお願いいたします。\n\n{{vendorName}}\n{{assigneeName}}`,
  },
  commission_payment_completed: {
    subject: '【報酬振込完了のお知らせ】{{dealTitle}} / {{amount}}',
    body: `{{recipientName}} 様\n\nご担当いただきました案件の報酬を振込にて完了しましたのでお知らせいたします。\n\n商談: {{dealTitle}}\n顧客: {{contactName}}\n商品: {{productName}}\n報酬額: {{amount}}\n振込先: {{bankInfo}}\n振込完了日: {{paidOutAt}}\n\n引き続きどうぞよろしくお願いいたします。\n\n{{vendorName}}`,
  },
  commission_invoice_received: {
    subject: '【請求書受領】{{recipientName}} さん / {{invoiceNumber}} / {{total}}',
    body: `経理担当者様\n\nSA「{{recipientName}}」より請求書が発行されました。\n\n請求番号: {{invoiceNumber}}\n対象期間: {{periodLabel}}\n件数: {{lineCount}} 件\n小計 (税抜): {{subtotal}}\n消費税: {{tax}}\n合計: {{total}}\n\n請求書 (公開URL):\n{{publicUrl}}\n\n{{vendorName}}`,
  },
  commission_invoice_paid: {
    subject: '【一括振込完了】{{invoiceNumber}} / {{total}}',
    body: `{{recipientName}} 様\n\n請求書「{{invoiceNumber}}」の振込が完了しましたのでお知らせいたします。\n\n件数: {{lineCount}} 件\n合計: {{total}}\n振込先: {{bankInfo}}\n振込完了日: {{paidOutAt}}\n\n引き続きどうぞよろしくお願いいたします。\n\n{{vendorName}}`,
  },
  commission_invoice_reminder: {
    subject: '【請求書発行のお願い】未請求 {{pendingCount}} 件 / 合計 {{totalAmount}}',
    body: `{{recipientName}} 様\n\n下記の報酬について、請求書発行をお願いいたします。\n\n件数: {{pendingCount}} 件\n合計 (税抜): {{totalAmount}}\n\n詳細・請求書発行は GoEnHub の「セールス専用」→「担当商談」→「請求可能」タブから操作してください:\n{{invoicePageUrl}}\n\n{{vendorName}}`,
  },
  // v3.2.22
  commission_payment_request: {
    subject: '【報酬支払い申請】{{recipientName}} さん / {{dealTitle}} / {{amount}}',
    body: `経理担当者様\n\n下記の報酬支払いをお願いします。\n\n商談: {{dealTitle}}\n顧客: {{contactName}}\n受取人: {{recipientName}} ({{recipientType}})\n入金日: {{paidAt}}\n報酬額: {{amount}}\n計算式: {{formula}}\n振込先: {{bankInfo}}\n\n報酬セット: {{commissionSetName}}\n商品: {{productName}}\n\n{{fallbackBody}}\n\n{{vendorName}}`,
  },
  form_submitted: {
    subject: '【{{vendorName}}】お申込みを受付けました - {{formTitle}}',
    body: `{{customerName}} 様\n\nこのたびは「{{formTitle}}」へのお申込みありがとうございます。\n下記内容で受付けました。担当より追ってご連絡いたします。\n\n受付日時: {{submittedAt}}\nメールアドレス: {{customerEmail}}\n\nしばらくお待ちください。\n\n{{vendorName}}`,
  },
  deal_lost: {
    subject: '【失注確定】{{dealTitle}}',
    body: `商談「{{dealTitle}}」が失注として確定しました。\n\n顧客: {{customerName}} ({{companyName}})\n失注理由: {{lostReason}}\n失注見積 ID: {{lostQuoteId}}\n期待金額: {{expectedAmount}}\n担当: {{assigneeName}}\n\n詳細: {{adminUrl}}\n\n{{vendorName}}`,
  },
  commission_record_created: {
    subject: '【報酬発生】{{dealTitle}} / {{amount}}',
    body: `{{recipientName}} 様\n\nご担当いただいた案件で報酬が発生しましたのでお知らせいたします。\n\n商談: {{dealTitle}}\n顧客: {{contactName}}\n商品: {{productName}}\n報酬額: {{amount}}\n入金確定日: {{paidAt}}\n適用報酬セット: {{commissionSetName}}\n\n振込までしばらくお待ちください。\n\n{{vendorName}}`,
  },
  // v3.2.25 (同意ステップ)
  consent_send: {
    subject: '【{{vendorName}}】同意書ご確認のお願い ({{consentTitle}})',
    body: `{{customerName}} 様\n\nこのたびはご契約ありがとうございます。\n下記の同意書のご確認とサインをお願いいたします。\n\n商談: {{dealTitle}}\n同意書: {{consentTitle}}\n\nサインページ:\n{{publicUrl}}\n\n上記 URL より同意書をご確認のうえ、ご署名をお願いいたします。\nご不明な点がございましたら、お気軽にお問い合わせください。\n\n{{vendorName}}`,
  },
  consent_signed: {
    subject: '【同意書サイン提出】{{dealTitle}} / {{customerName}} 様',
    body: `お疲れさまです。\n\n下記の同意書にサインが提出されました。\n\n商談: {{dealTitle}}\n顧客: {{customerName}} ({{companyName}})\n同意書: {{consentTitle}}\nサイン日: {{signedAt}}\n\n商談ステージは自動的に「同意済」に進行しました。\n\n{{vendorName}}`,
  },
  // v3.2.30 (外部SA管理) — {{footer}} は会社情報から自動組立
  sa_provisional: {
    subject: '【{{vendorName}}】セールスエージェント仮登録のご案内',
    body: `{{saName}} 様\n\nこのたびはセールスエージェントへお申し込みいただきありがとうございます。\n仮登録を受け付けいたしました。\n\n本登録までに下記のお手続きをお願いいたします。\n\n1. 弊社発行の同意書へのご署名・ご返送\n2. 登録費のお振込\n\n上記 2 点を確認でき次第、登録完了のご案内をお送りいたします。\nご不明な点がございましたらお気軽にお問い合わせください。\n\n{{footer}}`,
  },
  sa_registered: {
    subject: '【{{vendorName}}】セールスエージェント登録完了のお知らせ',
    body: `{{saName}} 様\n\n同意書・登録費の確認が取れましたので、登録完了となりましたことをお知らせいたします。\n\n契約期間: {{contractStartDate}} 〜 {{contractEndDate}} (第{{contractTerm}}期)\n\nつきましては、活動開始の前に弊社のセールスエージェント説明会へのご参加をお願いいたします。\n日程は別途ご案内いたします。\n\n今後ともよろしくお願いいたします。\n\n{{footer}}`,
  },
  sa_active: {
    subject: '【{{vendorName}}】活動開始のご案内',
    body: `{{saName}} 様\n\n説明会の受講を確認いたしました。本日より活動可能となります。\n\n契約期間: {{contractStartDate}} 〜 {{contractEndDate}} (第{{contractTerm}}期)\n\nセールスエージェント専用ポータルより、顧客登録・商談・資料・報酬計算などをご利用いただけます。\nこれからどうぞよろしくお願いいたします。\n\n{{footer}}`,
  },
  sa_renewal_reminder: {
    subject: '【{{vendorName}}】セールスエージェント契約更新のご案内',
    body: `{{saName}} 様\n\nいつもお世話になっております。\nご契約期間の満了が近づいてまいりましたので、契約更新のご案内をお送りいたします。\n\n現契約 終了日: {{contractEndDate}} (第{{contractTerm}}期)\n更新料: {{fee}}\n\n継続をご希望の場合は、SA ポータルの「更新」ボタンよりお申し込みのうえ、更新料のお振込をお願いいたします。\n入金の確認後、新しい契約期間へ更新いたします。\n\n{{footer}}`,
  },
  sa_renewal_due_internal: {
    subject: '【SA更新】対応が必要です: {{saName}} 様 (終了 {{contractEndDate}})',
    body: `SA 契約の更新対応が必要です。\n\nSA: {{saName}} ({{saEmail}})\n現契約 終了日: {{contractEndDate}} (第{{contractTerm}}期)\n残り日数: {{daysLeft}}\n契機: {{reason}}\n\n管理画面 > SA管理 で「更新」→ 更新料の入金確認 → 受講確認の順に進めてください。\n{{adminUrl}}\n\n{{vendorName}}`,
  },
  sa_renewed: {
    subject: '【{{vendorName}}】契約更新完了のお知らせ',
    body: `{{saName}} 様\n\n契約更新のお手続きが完了いたしました。引き続きよろしくお願いいたします。\n\n新しい契約期間: {{contractStartDate}} 〜 {{contractEndDate}} (第{{contractTerm}}期)\n\n{{footer}}`,
  },
  sa_customer_retention_reminder: {
    subject: '【{{vendorName}}】担当顧客の保有期限が近づいています ({{customerCount}}件)',
    body: `{{saName}} 様\n\nいつもお世話になっております。\n以下のお客様について、担当顧客としての保有期限が近づいています。\n\n{{customerList}}\n\n保有期間は{{retentionMonths}}ヶ月です。期限までにご担当の商談でご入金がありますと、そのご入金日を起算に期限が自動でリセットされます。\n期限を過ぎますと担当から外れますので、フォローのご検討をお願いいたします。\n\n担当顧客の一覧はこちら:\n{{saPortalUrl}}\n\n{{footer}}`,
  },
};

interface EditState {
  open: boolean;
  /** 編集対象。新規作成時は null */
  existing: BusinessMailTemplateRecord | null;
  /** 新規作成時に初期値として渡す eventType (null = 未アサイン) */
  initialEventType?: BusinessMailEvent | null;
  /** 新規作成時に初期値として渡す applicable (複製時に使用) */
  initialApplicable?: string[] | null;
  /** 新規作成時に初期値として渡す label */
  initialLabel?: string | null;
  /** 複製元 ID (新規 templateId にしない、UUID 自動採番) */
  duplicateSourceMail?: { subject: string; body: string };
}

/** eventType 変更ダイアログの state */
interface EventTypePickerState {
  open: boolean;
  template: BusinessMailTemplateRecord | null;
}

/** 複製ダイアログの state */
interface DuplicateState {
  open: boolean;
  source: BusinessMailTemplateRecord | null;
}

/**
 * 単独ページ用コンテナ。Screen ラッパ付き。
 * /sales/settings の「業務通知テンプレ」タブから埋め込み利用される。
 */
export function BusinessMailTemplatesPage() {
  return (
    <Screen
      title="業務通知テンプレート"
      description="営業フローの各イベントで自動送信されるメール + Google Chat 通知の内容を管理します。{{変数}} で顧客名・金額などを差し込めます。"
    >
      <BusinessMailTemplatesContent />
    </Screen>
  );
}

/** 共通カテゴリタブのキー */
const COMMON_TAB = '__common__';
/** 「全部」タブのキー */
const ALL_TAB = '__all__';

/** 競合通知 toast を組み立てる */
function buildConflictMessage(
  conflicts: UpsertConflict[],
  templates: BusinessMailTemplateRecord[],
  categoryNameMap: Map<string, string>,
): string | null {
  if (conflicts.length === 0) return null;
  const lines: string[] = [];
  for (const c of conflicts) {
    const t = templates.find((x) => x.id === c.templateId);
    const label =
      t?.label ||
      (t?.eventType ? BUSINESS_MAIL_EVENT_LABEL[t.eventType] : t?.id) ||
      c.templateId.slice(0, 8);
    if (c.unassigned) {
      lines.push(`「${label}」をトリガー未アサインに変更しました`);
    } else {
      const remaining = c.remainingCategoryIds
        .map((cid) => categoryNameMap.get(cid) ?? cid)
        .join(', ');
      lines.push(`「${label}」のカテゴリ割当を [${remaining}] に縮小しました`);
    }
  }
  return lines.join('\n');
}

export function BusinessMailTemplatesContent() {
  const { data, isLoading } = useBusinessMailTemplates();
  const upsert = useUpsertBusinessMailTemplate();
  const deleteMutation = useDeleteBusinessMailTemplate();
  const endpoints = useChatEndpoints('google_chat');
  const categoriesQuery = useProductCategories({ includeInactive: false });
  const [edit, setEdit] = useState<EditState>({
    open: false,
    existing: null,
  });
  const [eventTypePicker, setEventTypePicker] = useState<EventTypePickerState>({
    open: false,
    template: null,
  });
  const [duplicate, setDuplicate] = useState<DuplicateState>({
    open: false,
    source: null,
  });
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const [keyword, setKeyword] = useState('');

  const allTemplates = useMemo(() => data ?? [], [data]);

  // カテゴリ ID → name の lookup
  const categoryNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categoriesQuery.data ?? []) m.set(c.id, c.name);
    return m;
  }, [categoriesQuery.data]);

  /**
   * テンプレ単位の表示エントリ。
   * 各テンプレが 1 行で表示される。
   * eventType が同じ複数テンプレも別行として並ぶ。
   */
  const templateEntries = useMemo(() => {
    return allTemplates.map((t) => {
      const evt = t.eventType;
      const applicable = t.applicableCategoryIds ?? null;
      const audience: BusinessNotificationAudience =
        t.audience ?? (evt ? EVENT_AUDIENCE_DEFAULTS[evt] : 'internal');
      const baseLabel = evt ? BUSINESS_MAIL_EVENT_LABEL[evt] : '(未アサイン)';
      const displayLabel = t.label && t.label.length > 0 ? `${t.label}（${baseLabel}）` : baseLabel;
      const timing = evt ? EVENT_TIMING_LABEL[evt] : 'トリガー未アサイン';
      return {
        template: t,
        eventType: evt,
        baseLabel,
        displayLabel,
        timing,
        applicable,
        audience,
      };
    });
  }, [allTemplates]);

  /** eventType 単位のカウント (どの eventType に何件あるか、未配線判定用) */
  const eventTypeCounts = useMemo(() => {
    const m = new Map<BusinessMailEvent, number>();
    for (const t of allTemplates) {
      if (t.eventType) m.set(t.eventType, (m.get(t.eventType) ?? 0) + 1);
    }
    return m;
  }, [allTemplates]);

  /** テンプレが 1 件も無い eventType (プレースホルダ表示用) */
  const missingEventTypes = useMemo(() => {
    return ALL_EVENTS.filter((evt) => !eventTypeCounts.has(evt));
  }, [eventTypeCounts]);

  // タブ + 検索でフィルタ
  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    return templateEntries.filter((e) => {
      // タブフィルタ
      if (activeTab === ALL_TAB) {
        // 通る
      } else if (activeTab === COMMON_TAB) {
        if (e.applicable && e.applicable.length > 0) return false;
      } else {
        if (!e.applicable || !e.applicable.includes(activeTab)) return false;
      }
      // 検索フィルタ
      if (!k) return true;
      if (e.displayLabel.toLowerCase().includes(k)) return true;
      if (e.timing.toLowerCase().includes(k)) return true;
      if (e.template?.mail?.subject?.toLowerCase().includes(k)) return true;
      if (e.template?.mail?.body?.toLowerCase().includes(k)) return true;
      if (e.template?.label?.toLowerCase().includes(k)) return true;
      // カテゴリ名検索
      if (e.applicable) {
        for (const cid of e.applicable) {
          const name = categoryNameMap.get(cid);
          if (name && name.toLowerCase().includes(k)) return true;
        }
      }
      return false;
    });
  }, [templateEntries, activeTab, keyword, categoryNameMap]);

  /**
   * カテゴリ割当の保存。upsert callable で重複解消も併発させる。
   */
  const handleSaveCategories = async (
    existing: BusinessMailTemplateRecord,
    applicableCategoryIds: string[] | null,
  ) => {
    try {
      const res = await upsert.mutateAsync({
        templateId: existing.id,
        eventType: existing.eventType,
        label: existing.label ?? null,
        mail: existing.mail,
        chat: existing.chat,
        applicableCategoryIds,
        audience: existing.audience,
      });
      const conflictMsg = buildConflictMessage(res.conflicts, allTemplates, categoryNameMap);
      toast.success('カテゴリ設定を保存しました');
      if (conflictMsg) toast.info(conflictMsg);
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  /** eventType (トリガー) 変更の保存 */
  const handleChangeEventType = async (
    existing: BusinessMailTemplateRecord,
    nextEventType: BusinessMailEvent | null,
  ) => {
    try {
      const res = await upsert.mutateAsync({
        templateId: existing.id,
        eventType: nextEventType,
        label: existing.label ?? null,
        mail: existing.mail,
        chat: existing.chat,
        applicableCategoryIds: existing.applicableCategoryIds ?? null,
        audience: existing.audience,
      });
      const conflictMsg = buildConflictMessage(res.conflicts, allTemplates, categoryNameMap);
      toast.success('トリガーを変更しました');
      if (conflictMsg) toast.info(conflictMsg);
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  /** 複製の保存。新規 UUID + 指定カテゴリで作成 */
  const handleDuplicate = async (
    source: BusinessMailTemplateRecord,
    label: string,
    applicableCategoryIds: string[] | null,
  ) => {
    try {
      const res = await upsert.mutateAsync({
        // templateId 未指定 → サーバー側で UUID 自動採番
        eventType: source.eventType,
        label,
        mail: source.mail,
        chat: source.chat,
        applicableCategoryIds,
        audience: source.audience,
      });
      const conflictMsg = buildConflictMessage(res.conflicts, allTemplates, categoryNameMap);
      toast.success('テンプレを複製しました');
      if (conflictMsg) toast.info(conflictMsg);
    } catch (err) {
      toast.error(`複製失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  /** テンプレ削除 */
  const handleDelete = async (t: BusinessMailTemplateRecord) => {
    const baseLabel = t.eventType ? BUSINESS_MAIL_EVENT_LABEL[t.eventType] : '(未アサイン)';
    const name = t.label ? `${t.label} (${baseLabel})` : baseLabel;
    if (!window.confirm(`「${name}」を削除します。元に戻せません。`)) return;
    try {
      await deleteMutation.mutateAsync(t.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <>
      {/* 検索 + 追加ボタン */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
          <Input
            type="search"
            placeholder="テンプレ名 / 件名 / 本文 / タイミング / カテゴリ名で検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() =>
            setEdit({
              open: true,
              existing: null,
              initialEventType: null,
              initialApplicable: null,
            })
          }
        >
          <Plus className="size-3.5" />
          テンプレート追加
        </Button>
      </div>

      {/* カテゴリタブ */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-3">
        <TabsList className="flex-wrap">
          <TabsTrigger value={ALL_TAB}>全部</TabsTrigger>
          <TabsTrigger value={COMMON_TAB}>共通</TabsTrigger>
          {(categoriesQuery.data ?? []).map((c) => (
            <TabsTrigger key={c.id} value={c.id}>
              {c.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-[var(--text-muted)] mb-1">
            {filtered.length} 件 / 全 {templateEntries.length} 件
          </div>
          {filtered.map(
            ({ template: t, eventType: evt, displayLabel, timing, applicable, audience }) => {
              const mailEnabled = !!t.mail.enabled;
              const chatEnabled = !!t.chat.enabled;
              const isCommon = !applicable || applicable.length === 0;
              const isUnassigned = evt === null;
              return (
                <div
                  key={t.id}
                  className={`rounded bg-[var(--bg-elevated)] border ${
                    isUnassigned
                      ? 'border-[var(--accent-amber,_#d4a373)]'
                      : 'border-[var(--border)]'
                  } hover:border-[var(--text-muted)] transition-colors p-3`}
                >
                  <div className="flex items-center gap-3">
                    <Bell className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-medium truncate">{displayLabel}</p>
                        {/* audience バッジ */}
                        <Badge variant={audience === 'customer' ? 'accent' : 'default'}>
                          <Users className="size-2.5 mr-0.5 inline" />
                          {audience === 'customer' ? 'お客様' : '社内'}
                        </Badge>
                        {/* タイミングバッジ (クリックで eventType 変更) */}
                        <button
                          type="button"
                          onClick={() => setEventTypePicker({ open: true, template: t })}
                          className={
                            'inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors ' +
                            (isUnassigned
                              ? 'bg-[var(--accent-amber-subtle,_rgba(212,163,115,0.15))] text-[var(--accent-amber,_#d4a373)] border-[var(--accent-amber,_#d4a373)]'
                              : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border-transparent hover:border-[var(--accent)] hover:text-[var(--accent)]')
                          }
                          title="クリックしてトリガーを変更"
                        >
                          <Clock className="size-2.5" />
                          {timing}
                        </button>
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate">
                        {t.mail.subject || '(件名未設定)'}
                      </p>
                    </div>
                    {/* メール 有効化アイコン */}
                    <button
                      type="button"
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors ${
                        mailEnabled
                          ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-medium'
                          : 'text-[var(--text-muted)] opacity-50'
                      }`}
                      title={mailEnabled ? 'メール送信: 有効' : 'メール送信: 無効'}
                      onClick={() => setEdit({ open: true, existing: t })}
                    >
                      <Mail className="size-3" />
                      メール
                    </button>
                    {/* チャット 有効化アイコン */}
                    <button
                      type="button"
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors ${
                        chatEnabled
                          ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-medium'
                          : 'text-[var(--text-muted)] opacity-50'
                      }`}
                      title={chatEnabled ? 'チャット通知: 有効' : 'チャット通知: 無効'}
                      onClick={() => setEdit({ open: true, existing: t })}
                    >
                      <MessageSquare className="size-3" />
                      チャット
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEdit({ open: true, existing: t })}
                    >
                      <Edit />
                      編集
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDuplicate({ open: true, source: t })}
                      title="このテンプレを複製"
                    >
                      <Copy className="size-3.5" />
                      複製
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(t)}
                      title="削除"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                  {/* カテゴリバッジ行 (クリックで編集ポップオーバー) */}
                  <div className="flex items-center gap-1.5 mt-2 ml-7 flex-wrap">
                    <CategoryAssignment
                      isCommon={isCommon}
                      applicable={applicable ?? []}
                      categories={categoriesQuery.data ?? []}
                      onSave={(next) => handleSaveCategories(t, next)}
                    />
                  </div>
                </div>
              );
            },
          )}

          {/* 未配線 eventType のプレースホルダー (全部タブで keyword なしの時のみ表示) */}
          {activeTab === ALL_TAB && !keyword && missingEventTypes.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--border)] space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
                テンプレ未作成のトリガー
              </div>
              {missingEventTypes.map((evt) => (
                <div
                  key={evt}
                  className="rounded bg-[var(--bg-surface)] border border-dashed border-[var(--border)] p-3 flex items-center gap-3"
                >
                  <Bell className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-[var(--text-secondary)] truncate">
                      {BUSINESS_MAIL_EVENT_LABEL[evt]}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)]">
                      <Clock className="size-2.5 inline mr-0.5" />
                      {EVENT_TIMING_LABEL[evt]}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setEdit({
                        open: true,
                        existing: null,
                        initialEventType: evt,
                        initialApplicable: null,
                      })
                    }
                  >
                    <Plus className="size-3.5" />
                    作成
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <TemplateEditDialog
        state={edit}
        endpoints={endpoints.data ?? []}
        categories={categoriesQuery.data ?? []}
        onClose={() => setEdit({ open: false, existing: null })}
        onSave={async (input) => {
          try {
            const res = await upsert.mutateAsync(input);
            const conflictMsg = buildConflictMessage(res.conflicts, allTemplates, categoryNameMap);
            toast.success('保存しました');
            if (conflictMsg) toast.info(conflictMsg);
            setEdit({ open: false, existing: null });
          } catch (err) {
            toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
          }
        }}
      />

      {/* eventType (トリガー) 変更ダイアログ */}
      <EventTypePickerDialog
        state={eventTypePicker}
        onClose={() => setEventTypePicker({ open: false, template: null })}
        onSave={async (next) => {
          if (!eventTypePicker.template) return;
          await handleChangeEventType(eventTypePicker.template, next);
          setEventTypePicker({ open: false, template: null });
        }}
      />

      {/* 複製ダイアログ */}
      <DuplicateDialog
        state={duplicate}
        categories={categoriesQuery.data ?? []}
        onClose={() => setDuplicate({ open: false, source: null })}
        onSave={async (label, applicable) => {
          if (!duplicate.source) return;
          await handleDuplicate(duplicate.source, label, applicable);
          setDuplicate({ open: false, source: null });
        }}
      />
    </>
  );
}

/**
 * 主軸カテゴリ割り当ての表示 + 編集ポップオーバー。
 * バッジクリックで開閉、チェックボックスで複数選択。空 = 共通。
 */
function CategoryAssignment({
  isCommon,
  applicable,
  categories,
  onSave,
}: {
  isCommon: boolean;
  applicable: string[];
  categories: Array<{ id: string; name: string }>;
  onSave: (next: string[] | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(applicable);

  useEffect(() => {
    setDraft(applicable);
  }, [applicable, open]);

  const toggleCategory = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    const next = draft.length > 0 ? draft : null;
    await onSave(next);
    setOpen(false);
  };

  return (
    <>
      {isCommon ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:bg-[var(--accent-subtle)] hover:text-[var(--accent)] transition-colors"
          title="クリックして主軸カテゴリを割り当て"
        >
          <Layers className="size-2.5" />
          共通 (全カテゴリで送信)
        </button>
      ) : (
        <>
          {applicable.map((cid) => {
            const name = categories.find((c) => c.id === cid)?.name ?? cid;
            return (
              <button
                key={cid}
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)] hover:bg-[var(--accent)] hover:text-white transition-colors"
                title="クリックしてカテゴリ割り当てを編集"
              >
                <Layers className="size-2.5" />
                {name}
              </button>
            );
          })}
        </>
      )}

      {/* 編集ポップオーバー (シンプルな Dialog として実装) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>主軸カテゴリ割り当て</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-[var(--text-muted)]">
              チェックしたカテゴリの Deal でのみこのテンプレが送信されます。
              全部外すと「共通」(全カテゴリで送信) になります。
            </p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] italic py-2">
                  主軸カテゴリが登録されていません
                </p>
              ) : (
                categories.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[var(--bg-hover)] rounded px-2 py-1.5"
                  >
                    <input
                      type="checkbox"
                      checked={draft.includes(c.id)}
                      onChange={() => toggleCategory(c.id)}
                      className="size-4"
                    />
                    <span>{c.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              キャンセル
            </Button>
            <Button type="button" variant="primary" onClick={handleSave}>
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────
// 編集ダイアログ (メール / チャット 2 タブ)
// ─────────────────────────────────────────────

interface SaveInput {
  /** 既存編集時は docID、新規・複製時は undefined (= サーバで UUID 採番) */
  templateId?: string;
  eventType: BusinessMailEvent | null;
  label?: string | null;
  mail: {
    enabled: boolean;
    subject: string;
    body: string;
    fromAccountId?: string | null;
  };
  chat: {
    enabled: boolean;
    type: BusinessChatFormat;
    text?: string | null;
    card?: {
      title: string;
      subtitle?: string | null;
      body: string;
      buttons: BusinessChatButton[];
    } | null;
    endpointId?: string | null;
  };
  /** 既存のカテゴリ割り当てを保持するため (編集ダイアログでは触らない) */
  applicableCategoryIds?: string[] | null;
  audience?: BusinessNotificationAudience;
}

function TemplateEditDialog({
  state,
  endpoints,
  categories,
  onClose,
  onSave,
}: {
  state: EditState;
  endpoints: Array<{ id: string; name: string; isActive: boolean }>;
  categories: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSave: (input: SaveInput) => Promise<void>;
}) {
  const { open, existing, initialEventType, initialApplicable, initialLabel } = state;
  // ─── meta state ───
  const [eventType, setEventType] = useState<BusinessMailEvent | null>(null);
  const [label, setLabel] = useState('');
  // 送信対象カテゴリ: '' = 共通(全カテゴリ) / '__multi__' = 既存の複数割当を維持 / それ以外 = 単一カテゴリID
  const MULTI = '__multi__';
  const [categoryValue, setCategoryValue] = useState<string>('');
  // '__multi__' 選択時に維持する元の複数カテゴリ配列
  const [multiApplicable, setMultiApplicable] = useState<string[] | null>(null);
  // ─── mail state ───
  const [mailEnabled, setMailEnabled] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // ─── chat state ───
  const [chatEnabled, setChatEnabled] = useState(false);
  const [chatType, setChatType] = useState<BusinessChatFormat>('text');
  const [chatText, setChatText] = useState('');
  const [cardTitle, setCardTitle] = useState('');
  const [cardSubtitle, setCardSubtitle] = useState('');
  const [cardBody, setCardBody] = useState('');
  const [cardButtons, setCardButtons] = useState<BusinessChatButton[]>([]);
  const [endpointId, setEndpointId] = useState<string>('');

  // applicableCategoryIds 配列 → ドロップダウンの初期値を決める
  // 0件 → '' (共通) / 1件 → そのID / 2件以上 → '__multi__' (元配列を維持)
  const initCategoryValue = (applicable: string[] | null | undefined): string => {
    const arr = Array.isArray(applicable) ? applicable : [];
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    return MULTI;
  };

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setEventType(existing.eventType);
      setLabel(existing.label ?? '');
      setCategoryValue(initCategoryValue(existing.applicableCategoryIds));
      setMultiApplicable(existing.applicableCategoryIds ?? null);
      setMailEnabled(existing.mail.enabled);
      setSubject(existing.mail.subject);
      setBody(existing.mail.body);
      setChatEnabled(existing.chat.enabled);
      setChatType(existing.chat.type);
      setChatText(existing.chat.text ?? '');
      setCardTitle(existing.chat.card?.title ?? '');
      setCardSubtitle(existing.chat.card?.subtitle ?? '');
      setCardBody(existing.chat.card?.body ?? '');
      setCardButtons(existing.chat.card?.buttons ?? []);
      setEndpointId(existing.chat.endpointId ?? '');
    } else {
      // 新規作成
      const initEvt = initialEventType ?? null;
      setEventType(initEvt);
      setLabel(initialLabel ?? '');
      setCategoryValue(initCategoryValue(initialApplicable));
      setMultiApplicable(initialApplicable ?? null);
      const def = initEvt ? DEFAULT_MAIL[initEvt] : null;
      setMailEnabled(true);
      setSubject(def?.subject ?? '');
      setBody(def?.body ?? '');
      setChatEnabled(false);
      setChatType('text');
      setChatText('');
      setCardTitle('');
      setCardSubtitle('');
      setCardBody('');
      setCardButtons([]);
      setEndpointId('');
    }
  }, [open, existing, initialEventType, initialLabel, initialApplicable]);

  const handleSubmit = async () => {
    if (mailEnabled && (!subject.trim() || !body.trim())) {
      toast.error('メール有効化時は件名と本文が必須です');
      return;
    }
    if (chatEnabled) {
      if (!endpointId) {
        toast.error('チャット有効化時は通知先を選択してください');
        return;
      }
      if (chatType === 'text' && !chatText.trim()) {
        toast.error('テキスト形式のチャット本文が必須です');
        return;
      }
      if (chatType === 'card' && (!cardTitle.trim() || !cardBody.trim())) {
        toast.error('カード形式はタイトルと本文が必須です');
        return;
      }
    }

    await onSave({
      templateId: existing?.id,
      eventType,
      label: label.trim() || null,
      mail: {
        enabled: mailEnabled,
        subject,
        body,
        fromAccountId: null,
      },
      chat: {
        enabled: chatEnabled,
        type: chatType,
        text: chatType === 'text' ? chatText : null,
        card:
          chatType === 'card'
            ? {
                title: cardTitle,
                subtitle: cardSubtitle.trim() || null,
                body: cardBody,
                buttons: cardButtons,
              }
            : null,
        endpointId: endpointId || null,
      },
      // ドロップダウンの選択からカテゴリ割り当てを決定
      //  '' = 共通(null) / '__multi__' = 元の複数割当を維持 / それ以外 = 単一カテゴリ
      applicableCategoryIds:
        categoryValue === ''
          ? null
          : categoryValue === MULTI
            ? (multiApplicable ?? null)
            : [categoryValue],
      audience: existing?.audience,
    });
  };

  const availableVars = eventType ? (BUSINESS_MAIL_AVAILABLE_VARS[eventType] ?? []) : [];

  // eventType ピッカー (新規追加 / 既存テンプレ両方で使う)
  const eventTypeOptions = ALL_EVENTS.map((e) => ({
    value: e,
    label: BUSINESS_MAIL_EVENT_LABEL[e],
  }));

  const addButton = () => {
    if (cardButtons.length >= 5) {
      toast.error('リンクボタンは最大 5 個までです');
      return;
    }
    setCardButtons([...cardButtons, { label: '', url: '' }]);
  };
  const updateButton = (i: number, patch: Partial<BusinessChatButton>) => {
    setCardButtons(cardButtons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  };
  const removeButton = (i: number) => {
    setCardButtons(cardButtons.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <Bell className="inline size-4 mr-1" />
            {existing
              ? eventType
                ? BUSINESS_MAIL_EVENT_LABEL[eventType]
                : '未アサインテンプレート'
              : '新規テンプレート作成'}
          </DialogTitle>
        </DialogHeader>

        {/* メタ情報 (eventType + label) */}
        <div className="space-y-3 mb-2 border-b border-[var(--border)] pb-3">
          <div>
            <Label className="text-xs">トリガー (どのタイミングで送信するか)</Label>
            <select
              value={eventType ?? ''}
              onChange={(e) =>
                setEventType(e.target.value === '' ? null : (e.target.value as BusinessMailEvent))
              }
              className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
            >
              <option value="">— 未アサイン (どのトリガーでも送信されない) —</option>
              {eventTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {eventType && (
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                <Clock className="size-2.5 inline mr-0.5" />
                {EVENT_TIMING_LABEL[eventType]}
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">送信対象カテゴリ (主軸カテゴリ)</Label>
            <select
              value={categoryValue}
              onChange={(e) => setCategoryValue(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
            >
              <option value="">共通 (全カテゴリで送信)</option>
              {categoryValue === MULTI && (
                <option value={MULTI}>
                  複数カテゴリを維持 (
                  {(multiApplicable ?? [])
                    .map((cid) => categories.find((c) => c.id === cid)?.name ?? cid)
                    .join('・')}
                  )
                </option>
              )}
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              <Layers className="size-2.5 inline mr-0.5" />
              特定カテゴリを選ぶと、その主軸カテゴリの商談だけがこのテンプレを使用します
              (未該当は共通テンプレへフォールバック)。共通にすると全カテゴリで送信されます。
            </p>
          </div>
          <div>
            <Label className="text-xs">識別ラベル (任意・同じトリガーの複数テンプレを区別)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="例: Bio-Well 用 / VIP 顧客用 / 紹介者経由"
            />
          </div>
        </div>

        <Tabs defaultValue="mail">
          <TabsList>
            <TabsTrigger value="mail">
              <Mail className="size-3.5 mr-1" />
              メール
            </TabsTrigger>
            <TabsTrigger value="chat">
              <MessageSquare className="size-3.5 mr-1" />
              チャット
            </TabsTrigger>
          </TabsList>

          {/* ─── メールタブ ─── */}
          <TabsContent value="mail">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch checked={mailEnabled} onChange={(e) => setMailEnabled(e.target.checked)} />
                <Label className="!mb-0">このメールを自動送信する</Label>
              </div>
              <div>
                <Label>件名</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div>
                <Label>本文</Label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm font-mono"
                />
              </div>
            </div>
          </TabsContent>

          {/* ─── チャットタブ ─── */}
          <TabsContent value="chat">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch checked={chatEnabled} onChange={(e) => setChatEnabled(e.target.checked)} />
                <Label className="!mb-0">Google Chat 通知を自動送信する</Label>
              </div>

              <div>
                <Label>通知先</Label>
                <select
                  value={endpointId}
                  onChange={(e) => setEndpointId(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
                >
                  <option value="">— 選択してください —</option>
                  {endpoints.map((ep) => (
                    <option key={ep.id} value={ep.id} disabled={!ep.isActive}>
                      {ep.name}
                      {!ep.isActive ? ' (無効)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  通知先の追加・編集は「営業設定 &gt; チャット通知先設定」から
                </p>
              </div>

              <div>
                <Label>フォーマット</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setChatType('text')}
                    className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                      chatType === 'text'
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                        : 'border-[var(--border)]'
                    }`}
                  >
                    テキスト
                  </button>
                  <button
                    type="button"
                    onClick={() => setChatType('card')}
                    className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                      chatType === 'card'
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                        : 'border-[var(--border)]'
                    }`}
                  >
                    カード
                  </button>
                </div>
              </div>

              {chatType === 'text' ? (
                <div>
                  <Label>本文</Label>
                  <textarea
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    rows={8}
                    placeholder="シンプルなチャット本文。{{var}} 形式で変数差し込み可"
                    className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm font-mono"
                  />
                </div>
              ) : (
                <div className="space-y-2 rounded-md border border-[var(--border)] p-3 bg-[var(--bg-elevated)]">
                  <div>
                    <Label className="text-xs">カードタイトル</Label>
                    <Input
                      value={cardTitle}
                      onChange={(e) => setCardTitle(e.target.value)}
                      placeholder="🔔 新規先行予約"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">サブタイトル (任意)</Label>
                    <Input
                      value={cardSubtitle}
                      onChange={(e) => setCardSubtitle(e.target.value)}
                      placeholder="TimeWaverHub"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">カード本文</Label>
                    <textarea
                      value={cardBody}
                      onChange={(e) => setCardBody(e.target.value)}
                      rows={6}
                      placeholder="お名前: {{customerName}}&#10;メール: {{customerEmail}}"
                      className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-surface)] border border-[var(--border)] rounded-sm font-mono"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="!mb-0 text-xs">リンクボタン ({cardButtons.length}/5)</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={addButton}>
                        <Plus className="size-3" />
                        追加
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {cardButtons.map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            value={b.label}
                            onChange={(e) => updateButton(i, { label: e.target.value })}
                            placeholder="ラベル"
                            className="flex-1"
                          />
                          <Input
                            value={b.url}
                            onChange={(e) => updateButton(i, { url: e.target.value })}
                            placeholder="https://..."
                            className="flex-[2] font-mono text-[11px]"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeButton(i)}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-2">
          <Label className="!mb-1">利用可能な変数</Label>
          <div className="flex flex-wrap gap-1.5">
            {availableVars.map((v) => (
              <code
                key={v}
                className="text-xs px-2 py-0.5 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm font-mono cursor-pointer hover:bg-[var(--bg-hover)]"
                onClick={() => {
                  navigator.clipboard.writeText(`{{${v}}}`);
                  toast.success(`{{${v}}} をコピーしました`);
                }}
              >
                {`{{${v}}}`}
              </code>
            ))}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">クリックでクリップボードにコピー</p>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// 2026-05-28: eventType (トリガー) 変更ダイアログ
// ─────────────────────────────────────────────

function EventTypePickerDialog({
  state,
  onClose,
  onSave,
}: {
  state: EventTypePickerState;
  onClose: () => void;
  onSave: (next: BusinessMailEvent | null) => Promise<void>;
}) {
  const { open, template } = state;
  const [draft, setDraft] = useState<BusinessMailEvent | null>(null);

  useEffect(() => {
    if (open && template) setDraft(template.eventType);
  }, [open, template]);

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>トリガーを変更</DialogTitle>
          <DialogDescription>
            このテンプレが「いつ自動送信されるか」を変更します。同じカテゴリで競合する既存テンプレは自動的にトリガー未アサインに変更されます。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">トリガー</Label>
          <select
            value={draft ?? ''}
            onChange={(e) =>
              setDraft(e.target.value === '' ? null : (e.target.value as BusinessMailEvent))
            }
            className="w-full px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
          >
            <option value="">— 未アサイン (送信しない) —</option>
            {ALL_EVENTS.map((e) => (
              <option key={e} value={e}>
                {BUSINESS_MAIL_EVENT_LABEL[e]}
              </option>
            ))}
          </select>
          {draft && (
            <p className="text-[10px] text-[var(--text-muted)]">
              <Clock className="size-2.5 inline mr-0.5" />
              {EVENT_TIMING_LABEL[draft]}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={() => onSave(draft)}>
            <Save className="size-4" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// 2026-05-28: 複製ダイアログ (どのカテゴリーに追加しますか？)
// ─────────────────────────────────────────────

function DuplicateDialog({
  state,
  categories,
  onClose,
  onSave,
}: {
  state: DuplicateState;
  categories: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSave: (label: string, applicable: string[] | null) => Promise<void>;
}) {
  const { open, source } = state;
  const [label, setLabel] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !source) return;
    const baseName = source.label
      ? `${source.label} (複製)`
      : source.eventType
        ? `${BUSINESS_MAIL_EVENT_LABEL[source.eventType]} (複製)`
        : 'テンプレ (複製)';
    setLabel(baseName);
    setSelected([]);
  }, [open, source]);

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  if (!source) return null;

  const handleSubmit = async () => {
    if (!label.trim()) {
      toast.error('識別ラベルを入力してください');
      return;
    }
    const applicable = selected.length > 0 ? selected : null;
    await onSave(label.trim(), applicable);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>テンプレートを複製</DialogTitle>
          <DialogDescription>
            「
            {source.label ||
              (source.eventType ? BUSINESS_MAIL_EVENT_LABEL[source.eventType] : '未アサイン')}
            」のコピーを作成します。どのカテゴリーに追加しますか？
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">識別ラベル</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="例: Bio-Well 用 成約確定"
            />
          </div>
          <div>
            <Label className="text-xs">適用カテゴリ (複数可、空 = 共通)</Label>
            <div className="space-y-1.5 max-h-48 overflow-y-auto rounded border border-[var(--border)] p-2">
              {categories.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] italic">
                  主軸カテゴリが登録されていません
                </p>
              ) : (
                categories.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[var(--bg-hover)] rounded px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(c.id)}
                      onChange={() => toggle(c.id)}
                      className="size-4"
                    />
                    <span>{c.name}</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              既存テンプレと同じカテゴリを選んだ場合、既存テンプレからそのカテゴリは自動的に外されます（重複防止）。
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit}>
            <Copy className="size-4" />
            複製を作成
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
